/**
 * KanbanBoard — M8 (+ todos #5: группировка по атрибуту справочника).
 *
 * Базовый режим: колонки — опции поля `columnField` (тип select) или уникальные
 * значения поля (тип reference/text). Карточки drag&drop через @dnd-kit/sortable:
 * можно переставлять карточки ВНУТРИ колонки (ручной порядок, общий для всех —
 * пишется в глобальный `items.sort_order`) и переносить МЕЖДУ колонок с точной
 * позицией. При drop — `onCardDrop({ itemId, fromColumn, toColumn, orderedIdsInTarget })`:
 * смена колонки = смена статуса, порядок = reorder по слотам sort_order.
 *
 * Path-режим: если в config задан `columnFieldPath` (цепочка slug'ов колонок
 * справочников 1..5), группировка идёт по значению на конце цепочки —
 * через walkRefAttrPath. Drag-and-drop отключён, т.к. лист цепочки живёт в
 * справочнике, а не в записи (см. todos.md #5).
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import type { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Avatar, Button, Spin, Tooltip } from 'antd';
import { CalendarOutlined, ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import type { FieldDefinition, ItemRow, KanbanViewConfig } from '@lens/shared';
import { resolveTitleField } from '@lens/shared';
import { renderFieldValue } from '../../shared/fields/renderFieldValue.tsx';
import { useWhiteboardBoards } from '../whiteboard/useWhiteboardSummary.ts';
import { WhiteboardBadge } from '../whiteboard/WhiteboardBadge.tsx';
import { useNotifications } from '../notifications/NotificationsContext.tsx';
import {
  type DictColumnLite,
  type RefAttrCtx,
  getFinalRefAttrColumn,
  walkRefAttrPath,
} from '../spaces/refAttrPath.ts';
import { radius, spacing, statusCategory } from '../../shared/theme/tokens.ts';

// ─── helpers ────────────────────────────────────────────────────────────────

// Цвета категорий статусов — единый источник правды в shared/theme/tokens.
// Берём индикаторный цвет (.color) категории, синхронный с северной звездой.
const CATEGORY_COLORS: Record<string, string> = {
  backlog: statusCategory.backlog.color,
  active: statusCategory.active.color,
  done: statusCategory.done.color,
  cancelled: statusCategory.cancelled.color,
};

/** Синтетический slug: вывести тип записи как заголовок/подзаголовок карточки. */
export const ITEM_TYPE_SLUG = '_item_type';

function getItemFieldValue(item: ItemRow, fieldSlug: string): string {
  const val = item.fields[fieldSlug];
  if (val === null || val === undefined) return '';
  return String(val);
}

/**
 * Плоское превью текста для заголовка/подзаголовка карточки. Тело хранится как
 * markdown (tiptap-markdown), где перевод строки Shift+Enter сериализуется как
 * `\` в конце строки. В карточке текст выводится plain-text'ом и клампится, так
 * что сырой `\` виден пользователю, а сам перевод схлопывается в пробел. Убираем
 * маркер жёсткого переноса и схлопываем переводы строк в пробелы.
 */
function toCardPreview(s: string): string {
  return s
    .replace(/\\(\r?\n)/g, '$1') // hard-break `\` перед переводом строки — просто перевод
    .replace(/\s*\r?\n\s*/g, ' ') // переводы строк (с окружающими пробелами) → один пробел
    .replace(/ {2,}/g, ' ')
    .trim();
}

/** raw → отображаемая строка для path-режима с учётом типа листа цепочки. */
function formatPathLeaf(
  raw: unknown,
  finalCol: DictColumnLite | undefined,
  ctx: RefAttrCtx,
): string {
  if (raw === null || raw === undefined || raw === '') return '';
  if (!finalCol) return String(raw);
  if (finalCol.columnType === 'select' || finalCol.columnType === 'multi_select') {
    const opts =
      ((finalCol.config as { options?: Array<{ value: string; label: string }> } | null)
        ?.options) ?? [];
    return opts.find((o) => o.value === raw)?.label ?? String(raw);
  }
  if (finalCol.columnType === 'boolean') return raw ? 'Да' : 'Нет';
  if (finalCol.columnType === 'reference') {
    const nestedDictId = (finalCol.config as { dictionaryId?: string } | null)?.dictionaryId;
    const nestedOpts = nestedDictId ? ctx.dictionaryOptionsById[nestedDictId] : undefined;
    const labelById = new Map(nestedOpts?.map((o) => [o.value, o.label]) ?? []);
    const id = String(raw);
    return labelById.get(id) ?? ctx.resolvedRefs[id] ?? id;
  }
  return String(raw);
}

// ─── visual helpers (todos: kanban redesign) ────────────────────────────────

// Hover-эффекты карточек/колонок — через классы (инлайн-стили не умеют :hover).
const KANBAN_STYLES = `
.lens-kb-card{transition:box-shadow .15s,transform .15s,border-color .15s;}
.lens-kb-card:hover{box-shadow:var(--shadow-card-hover);transform:translateY(-2px);border-color:var(--color-primary);}
.lens-kb-col-scroll{scrollbar-width:thin;}
.lens-kb-col-scroll::-webkit-scrollbar{width:6px;}
.lens-kb-col-scroll::-webkit-scrollbar-thumb{background:var(--color-border);border-radius:3px;}
`;

const AVATAR_PALETTE = [
  '#1677ff', '#52c41a', '#fa8c16', '#eb2f96', '#722ed1',
  '#13c2c2', '#f5222d', '#faad14', '#2f54eb', '#a0d911',
];

function hashString(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return h;
}

function avatarColor(seed: string): string {
  return AVATAR_PALETTE[hashString(seed) % AVATAR_PALETTE.length]!;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] ?? '';
  const b = parts[1]?.[0] ?? '';
  return (a + b).toUpperCase() || '?';
}

const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/** Светлый фон-тинт из hex-цвета статуса (#rrggbb + alpha). Иначе — как есть. */
function tint(hex: string | undefined, alpha = '1f'): string {
  if (!hex) return 'var(--color-bg-container)';
  return /^#[0-9a-fA-F]{6}$/.test(hex) ? `${hex}${alpha}` : hex;
}

/**
 * Аватарки исполнителей (поле типа user). resolvedRefs: id → имя,
 * userAvatarById: id → URL фото. Есть фото — показываем его, иначе — инициалы.
 */
function AssigneeAvatars({
  value,
  resolvedRefs,
  userAvatarById,
}: {
  value: unknown;
  resolvedRefs?: Record<string, string>;
  userAvatarById?: Record<string, string>;
}): JSX.Element | null {
  const ids = Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string')
    : typeof value === 'string' && value
      ? [value]
      : [];
  if (ids.length === 0) return null;
  return (
    <Avatar.Group max={{ count: 4, style: { color: '#595959', backgroundColor: '#f0f0f0', fontSize: 11 } }} size={24}>
      {ids.map((id) => {
        const name = resolvedRefs?.[id] ?? id;
        const src = userAvatarById?.[id];
        return (
          <Tooltip key={id} title={name}>
            <Avatar
              size={24}
              src={src || undefined}
              style={{ backgroundColor: avatarColor(id), fontSize: 11, fontWeight: 600 }}
            >
              {initials(name)}
            </Avatar>
          </Tooltip>
        );
      })}
    </Avatar.Group>
  );
}

/** Бейдж дедлайна с подсветкой: просрочено — красный, ≤3 дней — оранжевый. */
function DeadlineBadge({ value }: { value: unknown }): JSX.Element | null {
  if (typeof value !== 'string' || !value) return null;
  const due = new Date(value);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  const overdue = days < 0;
  let bg: string = 'var(--color-bg-layout)';
  let fg: string = 'var(--color-text-secondary)';
  if (overdue) {
    bg = 'var(--color-error-bg)';
    fg = 'var(--color-error)';
  } else if (days <= 3) {
    bg = 'var(--color-warning-bg)';
    fg = 'var(--color-warning)';
  }
  const label = due.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' });
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: 11,
        lineHeight: '18px',
        padding: `0 ${spacing.sm}px`,
        borderRadius: radius.lg,
        background: bg,
        color: fg,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {overdue ? <ExclamationCircleOutlined style={{ fontSize: 10 }} /> : <CalendarOutlined style={{ fontSize: 10 }} />}
      {label}
    </span>
  );
}

// ─── KanbanCard ─────────────────────────────────────────────────────────────

interface KanbanCardProps {
  item: ItemRow;
  config: KanbanViewConfig;
  fields: FieldDefinition[];
  isDragging?: boolean;
  resolvedRefs?: Record<string, string>;
  /** id пользователя → URL аватарки (для поля user). */
  userAvatarById?: Record<string, string>;
  /** Для multi-type view: id типа → имя. Если задано — на карточке показывается тег типа. */
  itemTypeNameById?: Record<string, string>;
  /** Цвет акцент-бара слева (цвет колонки/статуса). */
  accentColor?: string;
}

function KanbanCard({ item, config, fields, isDragging = false, resolvedRefs, userAvatarById, itemTypeNameById, accentColor }: KanbanCardProps): JSX.Element {
  // Из контекста, а не пропом: иначе тащить через KanbanColumn и
  // SortableCard/StaticCard ради одного флага. Вне провайдера — пустой дефолт.
  const { unreadByItem } = useNotifications();
  const noticeHints = unreadByItem[item.id]?.hints;
  const whiteboard = useWhiteboardBoards()[item.id];
  const itemTypeName = itemTypeNameById?.[item.itemTypeId];

  // Синтетический «slug» для вывода типа записи в заголовке/подзаголовке.
  const titleIsItemType = config.cardTitleField === ITEM_TYPE_SLUG;
  const subtitleIsItemType = config.cardSubtitleField === ITEM_TYPE_SLUG;

  // Заголовок карточки: явно настроенное в представлении поле, иначе —
  // общий резолвер поля-названия (config.isTitle → slug title/name → текст).
  const titleField = titleIsItemType
    ? undefined
    : (fields.find((f) => f.slug === config.cardTitleField) ?? resolveTitleField(fields));
  const subtitleField = (!subtitleIsItemType && config.cardSubtitleField)
    ? fields.find((f) => f.slug === config.cardSubtitleField)
    : undefined;
  const extraFields = config.cardExtraFields
    .map((slug) => fields.find((f) => f.slug === slug))
    .filter(Boolean) as FieldDefinition[];

  // reference-заголовок: значение — entryId (или массив), показываем display
  // справочника через resolvedRefs, а не сырой UUID.
  const resolveKanbanTitle = (): string => {
    if (!titleField) return item.id;
    if (titleField.fieldType === 'reference') {
      const raw = item.fields[titleField.slug];
      const toLabel = (v: unknown): string =>
        (v != null && resolvedRefs?.[String(v)]) || String(v ?? '');
      if (Array.isArray(raw)) return raw.map(toLabel).filter(Boolean).join(', ');
      return raw != null && raw !== '' ? toLabel(raw) : '';
    }
    return getItemFieldValue(item, titleField.slug);
  };
  const title = titleIsItemType ? (itemTypeName ?? '') : toCardPreview(resolveKanbanTitle());
  const subtitle = subtitleIsItemType
    ? (itemTypeName ?? undefined)
    : (subtitleField ? toCardPreview(getItemFieldValue(item, config.cardSubtitleField!)) : undefined);

  // Исполнителей (user) и дедлайны (date) рендерим спец-виджетами; остальное — как теги.
  const userFields = extraFields.filter((f) => f.fieldType === 'user');
  const dateFields = extraFields.filter((f) => f.fieldType === 'date');
  const otherFields = extraFields.filter((f) => f.fieldType !== 'user' && f.fieldType !== 'date');
  const hasFooter = userFields.length > 0 || dateFields.length > 0;
  const hasNotice = (noticeHints?.length ?? 0) > 0;

  return (
    <div
      className="lens-kb-card"
      style={{
        position: 'relative',
        background: 'var(--color-bg-container)',
        // Непрочитанное уведомление по записи — заметная рамка. Цветом, а не
        // толщиной: смена ширины бордера сдвинула бы содержимое карточки.
        border: isDragging
          ? '2px dashed var(--color-primary)'
          : hasNotice
            ? '1px solid var(--color-primary)'
            : '1px solid var(--color-border)',
        borderRadius: radius.md,
        padding: '10px 12px 10px 14px',
        marginBottom: spacing.sm,
        boxShadow: isDragging ? '0 8px 20px rgba(0,0,0,0.18)' : 'var(--shadow-card)',
        opacity: isDragging ? 0.6 : 1,
        cursor: 'grab',
        userSelect: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Цветной акцент-бар слева (цвет колонки/статуса) */}
      {accentColor ? (
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            background: accentColor,
          }}
        />
      ) : null}
      {/* Непрочитанное уведомление по записи: точка в углу + подсказка с
          причиной. Рендерится независимо от мета-строки — она бывает пустой
          (нет ключа и типа), а метка должна быть видна всегда. */}
      {hasNotice ? (
        <Tooltip title={noticeHints!.join(' · ')}>
          <span
            data-testid="kanban-card-notice"
            aria-label={noticeHints!.join(' · ')}
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'var(--color-primary)',
              zIndex: 1,
            }}
          />
        </Tooltip>
      ) : null}
      {/* Мета-строка: ключ записи (трассируемость) + опц. тег типа */}
      {item.displayKey || itemTypeName ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: spacing.sm,
            marginBottom: spacing.xs,
          }}
        >
          {item.displayKey ? (
            <span style={{ fontFamily: MONO_FONT, fontSize: 11, color: 'var(--color-text-secondary)' }}>
              {item.displayKey}
            </span>
          ) : <span />}
          {itemTypeName ? (
            <span
              style={{
                fontSize: 11,
                lineHeight: '16px',
                padding: `0 ${spacing.sm}px`,
                borderRadius: radius.lg,
                background: 'var(--color-bg-layout)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {itemTypeName}
            </span>
          ) : null}
        </div>
      ) : null}
      <div
        style={{
          fontWeight: 500,
          fontSize: 13,
          lineHeight: '18px',
          color: 'var(--color-text)',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          marginBottom: subtitle || otherFields.length || hasFooter ? 6 : 0,
        }}
      >
        {title || <span style={{ color: 'var(--color-text-secondary)' }}>—</span>}
        <WhiteboardBadge board={whiteboard} />
      </div>
      {subtitle ? (
        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: otherFields.length || hasFooter ? 6 : 0 }}>
          {subtitleField ? renderFieldValue(item.fields[config.cardSubtitleField!] ?? null, subtitleField, resolvedRefs) : subtitle}
        </div>
      ) : null}
      {otherFields.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: hasFooter ? 6 : 0 }}>
          {otherFields.map((f) => {
            const raw = item.fields[f.slug];
            if (raw === null || raw === undefined) return null;
            // reference/select уже рендерятся чипами — серая подложка вокруг
            // чипа даёт «чип в чипе», выводим как есть.
            const rendersAsChip = f.fieldType === 'reference' || f.fieldType === 'select' || f.fieldType === 'multi_select';
            if (rendersAsChip) {
              return <Fragment key={f.slug}>{renderFieldValue(raw, f, resolvedRefs)}</Fragment>;
            }
            return (
              <span
                key={f.slug}
                style={{
                  fontSize: 11,
                  lineHeight: '18px',
                  padding: `0 ${spacing.sm}px`,
                  borderRadius: radius.lg,
                  background: 'var(--color-bg-layout)',
                  color: 'var(--color-text-secondary)',
                  maxWidth: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                {renderFieldValue(raw, f, resolvedRefs)}
              </span>
            );
          })}
        </div>
      ) : null}
      {hasFooter ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            {userFields.map((f) => (
              <AssigneeAvatars key={f.slug} value={item.fields[f.slug]} resolvedRefs={resolvedRefs} userAvatarById={userAvatarById} />
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {dateFields.map((f) => (
              <DeadlineBadge key={f.slug} value={item.fields[f.slug]} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ─── SortableCard ────────────────────────────────────────────────────────────

/**
 * Префикс id droppable-контейнера колонки. Нужен, чтобы отличать «бросили на
 * колонку» (пустая область / конец списка) от «бросили на карточку» (id карточки —
 * UUID записи), и не спутать значение статуса-колонки с id записи.
 */
const COL_PREFIX = 'kbcol:';

function SortableCard({
  item,
  columnValue,
  config,
  fields,
  resolvedRefs,
  userAvatarById,
  itemTypeNameById,
  accentColor,
  onClick,
}: {
  item: ItemRow;
  columnValue: string;
  config: KanbanViewConfig;
  fields: FieldDefinition[];
  resolvedRefs?: Record<string, string>;
  userAvatarById?: Record<string, string>;
  itemTypeNameById?: Record<string, string>;
  accentColor?: string;
  onClick?: (item: ItemRow) => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    data: { type: 'card', columnValue },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        // Пока тащим — прячем «призрак» на месте (оставляя пустой слот-зазор),
        // саму карточку под курсором рисует DragOverlay.
        opacity: isDragging ? 0 : 1,
      }}
      {...attributes}
      {...listeners}
      onClick={() => onClick?.(item)}
    >
      <KanbanCard item={item} config={config} fields={fields} resolvedRefs={resolvedRefs} userAvatarById={userAvatarById} itemTypeNameById={itemTypeNameById} accentColor={accentColor} />
    </div>
  );
}

/** Карточка без drag-обработчиков (для path-режима, где drag отключён). */
function StaticCard({
  item,
  config,
  fields,
  resolvedRefs,
  userAvatarById,
  itemTypeNameById,
  accentColor,
  onClick,
}: {
  item: ItemRow;
  config: KanbanViewConfig;
  fields: FieldDefinition[];
  resolvedRefs?: Record<string, string>;
  userAvatarById?: Record<string, string>;
  itemTypeNameById?: Record<string, string>;
  accentColor?: string;
  onClick?: (item: ItemRow) => void;
}): JSX.Element {
  return (
    <div onClick={() => onClick?.(item)} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <KanbanCard item={item} config={config} fields={fields} resolvedRefs={resolvedRefs} userAvatarById={userAvatarById} itemTypeNameById={itemTypeNameById} accentColor={accentColor} />
    </div>
  );
}

// ─── KanbanColumn ────────────────────────────────────────────────────────────

interface KanbanColumnProps {
  columnValue: string;
  label: string;
  color?: string;
  items: ItemRow[];
  config: KanbanViewConfig;
  fields: FieldDefinition[];
  onAddCard?: (columnValue: string) => void;
  onCardClick?: (item: ItemRow) => void;
  resolvedRefs?: Record<string, string>;
  userAvatarById?: Record<string, string>;
  itemTypeNameById?: Record<string, string>;
  /** Если true — карточки не draggable, droppable отключён. */
  staticMode?: boolean;
}

function KanbanColumn({
  columnValue,
  label,
  color,
  items,
  config,
  fields,
  onAddCard,
  onCardClick,
  resolvedRefs,
  userAvatarById,
  itemTypeNameById,
  staticMode = false,
}: KanbanColumnProps): JSX.Element {
  const droppable = useDroppable({ id: COL_PREFIX + columnValue, disabled: staticMode });
  const { setNodeRef, isOver } = droppable;

  return (
    <div
      ref={setNodeRef}
      style={{
        // Адаптивная ширина: колонки равномерно делят доступную ширину
        // (flex-basis 0 + grow), но не уже minWidth и не шире maxWidth.
        // Мало колонок → растягиваются до maxWidth без пустоты справа;
        // много → ужимаются до minWidth, дальше — горизонтальный скролл доски.
        flex: '1 1 0',
        minWidth: 264,
        maxWidth: 360,
        background: isOver ? 'var(--color-primary-bg)' : 'var(--color-bg-layout)',
        borderRadius: radius.lg,
        borderTop: `3px solid ${color ?? 'var(--color-border)'}`,
        // Узкие внутренние отступы: между карточками соседних колонок и так
        // есть зазор доски, широкие поля тут только едят место.
        padding: `0 6px 6px`,
        transition: 'background 0.15s',
        boxShadow: isOver ? 'inset 0 0 0 2px var(--color-primary)' : 'none',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Column header — цветная шапка-band (тинт цвета статуса), sticky */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm,
          padding: '7px 11px',
          margin: `${spacing.sm}px 0`,
          borderRadius: radius.md,
          background: tint(color),
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        {color ? (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: color,
              flexShrink: 0,
            }}
          />
        ) : null}
        <span
          style={{
            fontWeight: 600,
            fontSize: 12,
            flex: 1,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: color ?? 'var(--color-text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
        <span
          data-testid="kanban-col-count"
          style={{
            background: 'var(--color-bg-container)',
            borderRadius: radius.lg,
            padding: '1px 8px',
            fontSize: 12,
            fontWeight: 600,
            color: color ?? 'var(--color-text-secondary)',
          }}
        >
          {items.length}
        </span>
      </div>

      {/* Cards (scrollable) */}
      <div className="lens-kb-col-scroll" style={{ minHeight: 40, overflowY: 'auto', flex: 1, maxHeight: 'calc(100vh - 240px)' }}>
        {items.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              color: 'var(--color-text-secondary)',
              fontSize: 12,
              padding: '16px 0',
              border: '1px dashed var(--color-border)',
              borderRadius: radius.md,
            }}
          >
            Пусто
          </div>
        ) : staticMode ? (
          items.map((item) => (
            <StaticCard
              key={item.id}
              item={item}
              config={config}
              fields={fields}
              resolvedRefs={resolvedRefs}
              userAvatarById={userAvatarById}
              itemTypeNameById={itemTypeNameById}
              accentColor={color}
              onClick={onCardClick}
            />
          ))
        ) : (
          <SortableContext items={items.map((it) => it.id)} strategy={verticalListSortingStrategy}>
            {items.map((item) => (
              <SortableCard
                key={item.id}
                item={item}
                columnValue={columnValue}
                config={config}
                fields={fields}
                resolvedRefs={resolvedRefs}
                userAvatarById={userAvatarById}
                itemTypeNameById={itemTypeNameById}
                accentColor={color}
                onClick={onCardClick}
              />
            ))}
          </SortableContext>
        )}
      </div>

      {/* Add card button */}
      {onAddCard ? (
        <Tooltip title={`Добавить в «${label}»`}>
          <Button
            size="small"
            type="text"
            icon={<PlusOutlined />}
            style={{ width: '100%', marginTop: spacing.xs, color: 'var(--color-text-secondary)' }}
            onClick={() => onAddCard(columnValue)}
          >
            Добавить
          </Button>
        </Tooltip>
      ) : null}
    </div>
  );
}

// ─── KanbanBoard ─────────────────────────────────────────────────────────────

/**
 * Итог перетаскивания карточки. `fromColumn`/`toColumn` — значения поля-колонки
 * (статуса) до и после; равны, если карточку переставили внутри той же колонки.
 * `orderedIdsInTarget` — полный новый порядок id карточек целевой колонки
 * (включая перемещённую). Пустая колонка «Без значения» имеет value `''`.
 */
export interface KanbanCardDropResult {
  itemId: string;
  fromColumn: string;
  toColumn: string;
  orderedIdsInTarget: string[];
}

export interface KanbanBoardProps {
  items: ItemRow[];
  fields: FieldDefinition[];
  config: KanbanViewConfig;
  loading?: boolean;
  onCardDrop: (result: KanbanCardDropResult) => Promise<void> | void;
  /**
   * Порядок карточек задан сортировкой (представления или личной): перенос
   * внутри колонки отключён, между колонками — работает. Сверху доски —
   * подсказка, почему карточки не переставляются.
   */
  orderLocked?: boolean;
  onAddCard?: (columnValue: string) => Promise<void>;
  onCardClick?: (item: ItemRow) => void;
  resolvedRefs?: Record<string, string>;
  /** id пользователя → URL аватарки (для полей user на карточках). */
  userAvatarById?: Record<string, string>;
  /** Для multi-type view: id типа → имя. Если задано — на карточках виден тег типа записи. */
  itemTypeNameById?: Record<string, string>;
  /** Контекст для path-режима группировки по атрибуту справочника. */
  dictionaryColumnsById?: Record<string, DictColumnLite[]>;
  dictionaryEntryValues?: Record<string, Record<string, unknown>>;
  dictionaryOptionsById?: Record<string, Array<{ value: string; label: string }>>;
}

export interface KanbanLayoutInput {
  items: ItemRow[];
  fields: FieldDefinition[];
  config: KanbanViewConfig;
  resolvedRefs?: Record<string, string>;
  dictionaryColumnsById?: Record<string, DictColumnLite[]>;
  dictionaryEntryValues?: Record<string, Record<string, unknown>>;
  dictionaryOptionsById?: Record<string, Array<{ value: string; label: string }>>;
}

export interface KanbanLayout {
  columnField: FieldDefinition | undefined;
  isPathMode: boolean;
  columns: Array<{ value: string; label: string; color?: string }>;
  itemsByColumn: Map<string, ItemRow[]>;
  /**
   * Плоский порядок карточек = обход колонок слева-направо, внутри колонки — в том
   * же порядке, что и в `items`. Ровно то, что видит глаз на доске. Используется
   * для навигации prev/next из карточки (todos: стрелки должны листать по порядку
   * канбана, а не по плоскому списку таблицы). В path-режиме запись может попасть
   * в несколько колонок — в плоском списке дедуп по id (берём первое вхождение).
   */
  orderedItems: ItemRow[];
}

/**
 * Чистое построение раскладки доски: колонки (в порядке отрисовки) и раскладка
 * карточек по ним. Единый источник правды и для рендера KanbanBoard, и для
 * порядка навигации в SpacePage — иначе стрелки листают не так, как лежат карточки.
 */
export function buildKanbanLayout({
  items,
  fields,
  config,
  resolvedRefs,
  dictionaryColumnsById,
  dictionaryEntryValues,
  dictionaryOptionsById,
}: KanbanLayoutInput): KanbanLayout {
  // Resolve column field
  const columnField = fields.find((f) => f.slug === config.columnField);

  const path = config.columnFieldPath ?? [];
  const isPathMode = path.length > 0 && columnField?.fieldType === 'reference';

  // Build columns
  const columns: Array<{ value: string; label: string; color?: string }> = [];
  // For path mode: itemId → set of bucket keys (item may belong to multiple groups via multi-ref).
  const pathBucketsByItem = new Map<string, Set<string>>();

  if (isPathMode) {
    const rootDictId = (columnField?.config as { dictionaryId?: string } | null)?.dictionaryId;
    const colsById = dictionaryColumnsById ?? {};
    const valsById = dictionaryEntryValues ?? {};
    const optsById = dictionaryOptionsById ?? {};
    const ctx: RefAttrCtx = {
      dictionaryEntryValues: valsById,
      dictionaryColumnsById: colsById,
      dictionaryOptionsById: optsById,
      resolvedRefs: resolvedRefs ?? {},
    };
    const finalCol = rootDictId ? getFinalRefAttrColumn(rootDictId, path, colsById) : undefined;

    const labelByValue = new Map<string, string>();
    let hasEmpty = false;
    for (const item of items) {
      const raw = item.fields[config.columnField];
      const ids: string[] = Array.isArray(raw)
        ? (raw as unknown[]).filter((v): v is string => typeof v === 'string')
        : typeof raw === 'string' && raw ? [raw] : [];
      const buckets = new Set<string>();
      if (ids.length > 0 && rootDictId) {
        const leaves = walkRefAttrPath(ids, path, rootDictId, ctx);
        for (const leaf of leaves) {
          const label = formatPathLeaf(leaf, finalCol, ctx);
          if (!label) continue;
          const key = String(leaf);
          buckets.add(key);
          if (!labelByValue.has(key)) labelByValue.set(key, label);
        }
      }
      if (buckets.size === 0) {
        hasEmpty = true;
        pathBucketsByItem.set(item.id, new Set(['']));
      } else {
        pathBucketsByItem.set(item.id, buckets);
      }
    }

    // Если лист — select/multi_select, добавим все опции (даже пустые) — как с полем-select.
    if (finalCol && (finalCol.columnType === 'select' || finalCol.columnType === 'multi_select')) {
      const opts =
        ((finalCol.config as { options?: Array<{ value: string; label: string; color?: string }> } | null)
          ?.options) ?? [];
      for (const opt of opts) {
        if (
          config.visibleColumnValues.length === 0 ||
          config.visibleColumnValues.includes(opt.value)
        ) {
          columns.push({ value: opt.value, label: opt.label, color: opt.color });
        }
      }
      // Добиваем фактическими значениями, которых нет в options.
      for (const [val, label] of labelByValue) {
        if (!columns.some((c) => c.value === val)) columns.push({ value: val, label });
      }
    } else {
      // Для прочих типов листа — колонки строим из фактически встретившихся значений.
      const sortedEntries = Array.from(labelByValue.entries()).sort((a, b) =>
        a[1].localeCompare(b[1], 'ru'),
      );
      for (const [val, label] of sortedEntries) {
        columns.push({ value: val, label });
      }
    }

    if (hasEmpty) columns.push({ value: '', label: 'Без значения' });
  } else if (columnField?.fieldType === 'select') {
    const cfg = columnField.config as {
      options?: Array<{ value: string; label: string; color?: string; category?: string }>;
    };
    (cfg.options ?? []).forEach((opt) => {
      if (
        config.visibleColumnValues.length === 0 ||
        config.visibleColumnValues.includes(opt.value)
      ) {
        columns.push({
          value: opt.value,
          label: opt.label,
          color: opt.color ?? (opt.category ? CATEGORY_COLORS[opt.category] : undefined),
        });
      }
    });
    // Add "no value" column for items without select value
    const hasEmpty = items.some((it) => !getItemFieldValue(it, config.columnField));
    if (hasEmpty) {
      columns.push({ value: '', label: 'Без значения' });
    }
  } else {
    // reference / user / text: build from distinct values in items.
    // Для reference/user значение — это id (логин/UUID), поэтому ярлык колонки
    // резолвим через resolvedRefs (название справочника / ФИО), иначе светим код.
    const seen = new Set<string>();
    for (const item of items) {
      const val = getItemFieldValue(item, config.columnField);
      if (val && !seen.has(val)) {
        seen.add(val);
        columns.push({ value: val, label: resolvedRefs?.[val] ?? val });
      }
    }
    // "No value" column
    const hasEmpty = items.some((it) => !getItemFieldValue(it, config.columnField));
    if (hasEmpty) columns.push({ value: '', label: 'Без значения' });
  }

  // Пользовательский порядок колонок (config.columnOrder). Значения, которых нет в
  // списке, идут после перечисленных, сохраняя исходный порядок; «Без значения»
  // без явной позиции — всегда последняя.
  const order = config.columnOrder ?? [];
  if (order.length > 0) {
    const pos = new Map(order.map((v, i) => [v, i]));
    const rank = (value: string, idx: number): number => {
      const p = pos.get(value);
      if (p != null) return p;
      if (value === '') return Number.MAX_SAFE_INTEGER;
      return order.length + idx;
    };
    const indexed = columns.map((c, idx) => ({ c, r: rank(c.value, idx) }));
    indexed.sort((a, b) => a.r - b.r);
    columns.splice(0, columns.length, ...indexed.map((x) => x.c));
  }

  // Group items by column
  const itemsByColumn = new Map<string, ItemRow[]>();
  for (const col of columns) {
    itemsByColumn.set(col.value, []);
  }
  if (isPathMode) {
    for (const item of items) {
      const buckets = pathBucketsByItem.get(item.id) ?? new Set(['']);
      for (const b of buckets) {
        const target = itemsByColumn.has(b) ? b : '';
        const arr = itemsByColumn.get(target);
        if (arr) arr.push(item);
      }
    }
  } else {
    for (const item of items) {
      const val = getItemFieldValue(item, config.columnField);
      const bucket = itemsByColumn.has(val) ? val : '';
      const arr = itemsByColumn.get(bucket);
      if (arr) arr.push(item);
    }
  }

  // Плоский порядок навигации — обход колонок слева-направо, внутри как в items.
  const orderedItems: ItemRow[] = [];
  const orderSeen = new Set<string>();
  for (const col of columns) {
    for (const it of itemsByColumn.get(col.value) ?? []) {
      if (orderSeen.has(it.id)) continue;
      orderSeen.add(it.id);
      orderedItems.push(it);
    }
  }

  return { columnField, isPathMode, columns, itemsByColumn, orderedItems };
}

/** Плоская карта «колонка → упорядоченные id карточек» из раскладки доски. */
function layoutToColMap(layout: KanbanLayout): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const col of layout.columns) {
    map[col.value] = (layout.itemsByColumn.get(col.value) ?? []).map((it) => it.id);
  }
  return map;
}

export function KanbanBoard({
  items,
  fields,
  config,
  loading = false,
  onCardDrop,
  orderLocked = false,
  onAddCard,
  onCardClick,
  resolvedRefs,
  userAvatarById,
  itemTypeNameById,
  dictionaryColumnsById,
  dictionaryEntryValues,
  dictionaryOptionsById,
}: KanbanBoardProps): JSX.Element {
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  // Раскладка доски (колонки + карточки по ним) — единый источник правды,
  // тот же, что даёт порядок навигации prev/next в SpacePage.
  const layout = useMemo(
    () =>
      buildKanbanLayout({
        items,
        fields,
        config,
        resolvedRefs,
        dictionaryColumnsById,
        dictionaryEntryValues,
        dictionaryOptionsById,
      }),
    [items, fields, config, resolvedRefs, dictionaryColumnsById, dictionaryEntryValues, dictionaryOptionsById],
  );
  const { columnField, isPathMode, columns, itemsByColumn } = layout;

  const itemById = useMemo(() => new Map(items.map((it) => [it.id, it])), [items]);

  // Колонка каждой записи ДО текущего перетаскивания (из props-раскладки) —
  // чтобы на drop понять, сменился ли статус.
  const originalColumnByItem = useMemo(() => {
    const m = new Map<string, string>();
    for (const col of columns) {
      for (const it of itemsByColumn.get(col.value) ?? []) m.set(it.id, col.value);
    }
    return m;
  }, [columns, itemsByColumn]);

  // Живой порядок id по колонкам во время перетаскивания. Ресинк при смене
  // props-раскладки: после сохранения/refetch родитель отдаёт новый порядок.
  // colRef — синхронное зеркало для чтения из обработчиков (setState асинхронен,
  // а onDragEnd читает результат последнего onDragOver).
  const [colItemIds, setColItemIds] = useState<Record<string, string[]>>(() => layoutToColMap(layout));
  const colRef = useRef<Record<string, string[]>>(colItemIds);
  useEffect(() => {
    const map = layoutToColMap(layout);
    colRef.current = map;
    setColItemIds(map);
  }, [layout]);

  const findColumn = (id: string): string | undefined => {
    for (const [col, ids] of Object.entries(colRef.current)) {
      if (ids.includes(id)) return col;
    }
    return undefined;
  };
  const columnOf = (overId: string): string | undefined =>
    overId.startsWith(COL_PREFIX) ? overId.slice(COL_PREFIX.length) : findColumn(overId);

  const resync = () => {
    const map = layoutToColMap(layout);
    colRef.current = map;
    setColItemIds(map);
  };

  const activeItem = activeItemId ? itemById.get(activeItemId) ?? null : null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveItemId(String(event.active.id));
  };

  // Перенос карточки в другую колонку по ходу перетаскивания (живой предпросмотр).
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    const from = findColumn(activeId);
    const to = columnOf(overId);
    if (from === undefined || to === undefined || from === to) return;
    setColItemIds((prev) => {
      const fromIds = prev[from] ?? [];
      const toIds = prev[to] ?? [];
      if (!fromIds.includes(activeId)) return prev;
      let insertAt = overId.startsWith(COL_PREFIX) ? toIds.length : toIds.indexOf(overId);
      if (insertAt < 0) insertAt = toIds.length;
      const next = {
        ...prev,
        [from]: fromIds.filter((id) => id !== activeId),
        [to]: [...toIds.slice(0, insertAt), activeId, ...toIds.slice(insertAt)],
      };
      colRef.current = next;
      return next;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveItemId(null);
    const { active, over } = event;
    if (!over) {
      resync();
      return;
    }
    const activeId = String(active.id);
    const overId = String(over.id);
    // После handleDragOver карточка уже лежит в целевой колонке.
    const targetCol = findColumn(activeId);
    if (targetCol === undefined) {
      resync();
      return;
    }

    // Внутри целевой колонки доводим позицию: если бросили на карточку — arrayMove.
    let finalIds = colRef.current[targetCol] ?? [];
    if (!overId.startsWith(COL_PREFIX)) {
      const oldIndex = finalIds.indexOf(activeId);
      const newIndex = finalIds.indexOf(overId);
      if (oldIndex >= 0 && newIndex >= 0 && oldIndex !== newIndex) {
        finalIds = arrayMove(finalIds, oldIndex, newIndex);
        const next = { ...colRef.current, [targetCol]: finalIds };
        colRef.current = next;
        setColItemIds(next);
      }
    }

    const originalCol = originalColumnByItem.get(activeId) ?? targetCol;
    const columnChanged = originalCol !== targetCol;
    const originalOrder = (itemsByColumn.get(targetCol) ?? []).map((it) => it.id);
    const orderChanged =
      finalIds.length !== originalOrder.length || finalIds.some((id, i) => id !== originalOrder[i]);

    // Ничего не изменилось (бросок на место) — откатываем локальный предпросмотр.
    // При заданной сортировке перестановка внутри колонки тоже не действие:
    // порядок задаёт сортировка, локальный предпросмотр откатываем.
    if (!columnChanged && (!orderChanged || orderLocked)) {
      resync();
      return;
    }

    void onCardDrop({
      itemId: activeId,
      fromColumn: originalCol,
      toColumn: targetCol,
      orderedIdsInTarget: finalIds,
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 64 }} data-testid="kanban-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!columnField) {
    return (
      <div style={{ color: 'var(--color-error)', padding: spacing.base }}>
        Поле-колонка «{config.columnField}» не найдено в типе записей.
      </div>
    );
  }

  const board = (
    <div
      data-testid="kanban-board"
      style={{
        display: 'flex',
        gap: spacing.sm,
        overflowX: 'auto',
        paddingBottom: spacing.md,
        alignItems: 'flex-start',
      }}
    >
      <style>{KANBAN_STYLES}</style>
      {columns.map((col) => (
        <KanbanColumn
          key={col.value}
          columnValue={col.value}
          label={col.label}
          color={col.color}
          // Живой порядок карточек колонки (обновляется по ходу перетаскивания).
          items={(colItemIds[col.value] ?? []).map((id) => itemById.get(id)).filter((it): it is ItemRow => it != null)}
          config={config}
          fields={fields}
          // Кнопка «Добавить» в path-режиме недоступна: значение лежит в справочнике,
          // и мы не можем однозначно подставить его в новую запись.
          onAddCard={!isPathMode && onAddCard ? (v) => void onAddCard(v) : undefined}
          onCardClick={onCardClick}
          resolvedRefs={resolvedRefs}
          userAvatarById={userAvatarById}
          itemTypeNameById={itemTypeNameById}
          staticMode={isPathMode}
        />
      ))}
    </div>
  );

  const lockHint = orderLocked && !isPathMode ? (
    <div
      data-testid="kanban-order-locked-hint"
      style={{ color: 'var(--color-text-secondary)', fontSize: 12, marginBottom: spacing.xs }}
    >
      Порядок карточек задан сортировкой — внутри колонки карточки не переставляются, перенос между колонками работает.
    </div>
  ) : null;

  if (isPathMode) {
    return board;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {lockHint}
      {board}
      <DragOverlay dropAnimation={null}>
        {activeItem ? (
          <KanbanCard item={activeItem} config={config} fields={fields} resolvedRefs={resolvedRefs} userAvatarById={userAvatarById} itemTypeNameById={itemTypeNameById} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
